import { useEffect } from 'react'
import { useQuery } from '@apollo/client/react'
import { gql } from '@apollo/client'

const GET_AGENT_STATUS = gql`
  query GetAgentStatus($clientId: ID!) {
    agentGoal(clientId: $clientId) {
      id
      goalType
      targetMetric
      targetValue
      currentValue
      deadline
      status
    }
    agentPersona(clientId: $clientId) {
      id
      name
      focusAreas
    }
    client(id: $clientId) {
      schedulingConfig {
        nextAutoRunAt
        lastAutoRunAt
        autoGenerate { enabled }
      }
    }
  }
`

interface AgentGoal {
  id: string
  goalType: string
  targetMetric: string
  targetValue: number
  currentValue: number
  deadline: string
  status: string
}

interface AgentPersona {
  id: string
  name: string
  focusAreas: string[]
}

interface ClientSchedule {
  schedulingConfig: {
    nextAutoRunAt: string | null
    lastAutoRunAt: string | null
    autoGenerate: { enabled: boolean } | null
  } | null
}

export function useAgentStatus(clientId: string) {
  const { data, loading, startPolling, stopPolling } = useQuery<{
    agentGoal: AgentGoal | null
    agentPersona: AgentPersona | null
    client: ClientSchedule | null
  }>(GET_AGENT_STATUS, { variables: { clientId }, skip: !clientId })

  const autoGenerate = data?.client?.schedulingConfig?.autoGenerate
  const nextAutoRunAt = data?.client?.schedulingConfig?.nextAutoRunAt ?? null
  const lastAutoRunAt = data?.client?.schedulingConfig?.lastAutoRunAt ?? null
  const enabled = autoGenerate?.enabled ?? false

  useEffect(() => {
    if (enabled) {
      startPolling(60_000)
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [enabled, startPolling, stopPolling])

  return {
    goal: data?.agentGoal ?? null,
    persona: data?.agentPersona ?? null,
    nextAutoRunAt: enabled ? nextAutoRunAt : null,
    lastAutoRunAt,
    loading,
  }
}
